import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Drawer, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import type { ICampaign } from '../../../redux/buzzbridge/types';
import { updateOffsiteCampaign } from '../../../redux/buzzbridge/actions';
import { updatingCampaignSelector } from '../../../redux/buzzbridge/selectors';
import { DraggableFieldList } from '../../../components/Common/DraggableFieldList';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_SURFACE_CONTAINER_LOW,
    BRAND_SURFACE_CONTAINER,
} from '../brandDashboardTokens';
import { FONT_ENUM } from '../../../types/global/fonts.types';

function FieldLabel({ children }: { children: React.ReactNode }) {
    return (
        <Typography
            sx={{
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#fff',
                mb: 1.5,
            }}>
            {children}
        </Typography>
    );
}

interface Props {
    open: boolean;
    onClose: () => void;
    campaign: ICampaign;
}

export function CampaignBriefPanel({ open, onClose, campaign }: Props) {
    const dispatch = useDispatch();
    const updating = useSelector(updatingCampaignSelector);

    const [objectives, setObjectives] = useState<string[]>([]);
    const [requirements, setRequirements] = useState<string[]>([]);
    const [ideas, setIdeas] = useState<string[]>([]);

    useEffect(() => {
        if (!open) return;
        setObjectives([...(campaign.objectives || [])]);
        setRequirements([...(campaign.requirements || [])]);
        setIdeas([...((campaign as any).ideas || [])]);
    }, [open, campaign]);

    const handleSave = () => {
        const arraysEqual = (a: string[], b: string[]) =>
            a.length === b.length && a.every((v, i) => v === b[i]);
        const body: Record<string, any> = {};

        const cleanObjectives = objectives.filter(Boolean);
        if (!arraysEqual(cleanObjectives, campaign.objectives || [])) body.objectives = cleanObjectives;

        const cleanRequirements = requirements.filter(Boolean);
        if (!arraysEqual(cleanRequirements, campaign.requirements || [])) body.requirements = cleanRequirements;

        const cleanIdeas = ideas.filter(s => String(s || '').trim());
        const origIdeas = ((campaign as any).ideas || []).filter((s: string) => String(s || '').trim());
        if (!arraysEqual(cleanIdeas, origIdeas)) body.ideas = cleanIdeas;

        if (Object.keys(body).length === 0) { onClose(); return; }
        dispatch(updateOffsiteCampaign({ campaignId: campaign._id, body }));
        onClose();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100vw', sm: 520 },
                    bgcolor: '#111',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2.5,
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16, color: '#fff' }}>
                    Content Brief
                </Typography>
                <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                    <FieldLabel>Campaign Objectives</FieldLabel>
                    <DraggableFieldList
                        items={objectives}
                        onItemsChange={setObjectives}
                        placeholder="Add objective..."
                    />
                </Box>

                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                    <FieldLabel>Requirements</FieldLabel>
                    <DraggableFieldList
                        items={requirements}
                        onItemsChange={setRequirements}
                        placeholder="Add requirement..."
                    />
                </Box>

                <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 3, borderRadius: 2 }}>
                    <FieldLabel>Creative Ideas</FieldLabel>
                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', mb: 1.5 }}>
                        Optional content angles or hooks shown to creators as inspiration.
                    </Typography>
                    <DraggableFieldList
                        items={ideas}
                        onItemsChange={setIdeas}
                        placeholder="Add creative idea..."
                    />
                </Box>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    px: 3,
                    py: 2.5,
                    borderTop: '1px solid rgba(255,255,255,0.06)',
                    bgcolor: `${BRAND_SURFACE_CONTAINER}80`,
                    display: 'flex',
                    gap: 2,
                    flexDirection: 'row-reverse',
                }}>
                <Button
                    onClick={handleSave}
                    disabled={updating}
                    sx={{
                        px: 4,
                        py: 1.5,
                        bgcolor: BRAND_LUXE_PRIMARY,
                        color: '#000',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        borderRadius: 3,
                        '&:hover': { bgcolor: '#e6b500' },
                        '&.Mui-disabled': { bgcolor: 'rgba(255,201,101,0.3)', color: 'rgba(0,0,0,0.4)' },
                        minWidth: 100,
                    }}>
                    {updating ? <CircularProgress size={16} sx={{ color: '#000' }} /> : 'Save'}
                </Button>
                <Button
                    onClick={onClose}
                    sx={{
                        px: 3,
                        py: 1.5,
                        color: 'rgba(255,255,255,0.6)',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        borderRadius: 3,
                        border: '1px solid rgba(255,255,255,0.1)',
                    }}>
                    Cancel
                </Button>
            </Box>
        </Drawer>
    );
}
